import React, { useCallback, useEffect, useState } from "react";
import AdminSectionHeader from "@/components/admin/AdminSectionHeader";
import AdminTable from "@/components/admin/AdminTable";
import CTAButton from "@/components/app/CTAButton";
import { StatusBadge } from "@/components/app/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { Trash2 } from "lucide-react";

export default function AdminPricing() {
    const [tcg, setTcg] = useState(null);
    const [categories, setCategories] = useState([]);
    const [blocks, setBlocks] = useState([]);
    const [rules, setRules] = useState([]);
    const [error, setError] = useState("");
    const [draft, setDraft] = useState({ mission_category_id: "", price_eur: 0.5, block_id: "", priority: 0, note: "" });

    const load = useCallback(async () => {
        try {
            const [tcgRes, catRes, bRes, rRes] = await Promise.all([
                api.get("/admin/catalog/tcgs"),
                api.get("/admin/catalog/mission-categories", { params: { tcg_slug: "pokemon" } }),
                api.get("/admin/catalog/blocks", { params: { tcg_slug: "pokemon" } }),
                api.get("/admin/catalog/pricing-rules", { params: { tcg_slug: "pokemon" } }),
            ]);
            setTcg(tcgRes.data.find((t) => t.slug === "pokemon"));
            setCategories(catRes.data);
            setBlocks(bRes.data);
            setRules(rRes.data);
        } catch (e) { setError(formatApiError(e)); }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function create() {
        if (!tcg || !draft.mission_category_id) return;
        try {
            await api.post("/admin/catalog/pricing-rules", {
                tcg_id: tcg._id,
                mission_category_id: draft.mission_category_id,
                block_id: draft.block_id || null,
                price_eur: Number(draft.price_eur),
                priority: draft.priority,
                note: draft.note || null,
            });
            setDraft({ mission_category_id: "", price_eur: 0.5, block_id: "", priority: 0, note: "" });
            await load();
        } catch (e) { setError(formatApiError(e)); }
    }
    async function del(r) {
        try { await api.delete(`/admin/catalog/pricing-rules/${r._id}`); await load(); }
        catch (e) { setError(formatApiError(e)); }
    }

    const catById = Object.fromEntries(categories.map((c) => [c._id, c]));
    const blockById = Object.fromEntries(blocks.map((b) => [b._id, b]));

    return (
        <>
            <AdminSectionHeader
                title="Pricing"
                description="Prix par catégorie mission, optionnellement scopé à un bloc. La règle la plus prioritaire l'emporte."
            />
            {error && <div className="mb-4 rounded-lg border border-md-danger/30 bg-md-danger/10 px-4 py-2 text-sm text-md-danger">{error}</div>}

            <div className="mb-6 rounded-2xl border border-md-border bg-md-surface p-6">
                <p className="md-overline mb-4">Nouvelle règle</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
                    <select value={draft.mission_category_id} onChange={(e) => setDraft({ ...draft, mission_category_id: e.target.value })} data-testid="pricing-cat" className="sm:col-span-2 rounded-lg border border-md-border bg-md-bg px-3 py-2 text-sm">
                        <option value="">Catégorie *</option>
                        {categories.map((c) => <option key={c._id} value={c._id}>{c.label}</option>)}
                    </select>
                    <select value={draft.block_id} onChange={(e) => setDraft({ ...draft, block_id: e.target.value })} data-testid="pricing-block" className="rounded-lg border border-md-border bg-md-bg px-3 py-2 text-sm">
                        <option value="">Tous blocs</option>
                        {blocks.filter((b) => b.is_active).map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
                    </select>
                    <input type="number" step="0.01" min="0" value={draft.price_eur} onChange={(e) => setDraft({ ...draft, price_eur: e.target.value })} data-testid="pricing-price" className="rounded-lg border border-md-border bg-md-bg px-3 py-2 text-sm" placeholder="Prix €" />
                    <input type="number" value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: Number(e.target.value) })} data-testid="pricing-priority" className="rounded-lg border border-md-border bg-md-bg px-3 py-2 text-sm" placeholder="Priorité" />
                    <CTAButton size="sm" onClick={create} disabled={!draft.mission_category_id} testId="pricing-create">Ajouter</CTAButton>
                </div>
            </div>

            <AdminTable
                emptyLabel="Aucune règle de prix. Crée la grille tarifaire ci-dessus."
                columns={[
                    { key: "priority", label: "Priorité", render: (r) => <span className="font-mono text-xs">{r.priority}</span> },
                    { key: "category", label: "Catégorie", render: (r) => (
                        <span className="inline-flex items-center gap-2">
                            <span className="inline-block h-3 w-3 rounded-full" style={{ background: catById[r.mission_category_id]?.color || "#ccc" }} />
                            {catById[r.mission_category_id]?.label || "?"}
                        </span>
                    )},
                    { key: "block", label: "Bloc", render: (r) => r.block_id ? blockById[r.block_id]?.name || "?" : <span className="italic text-md-text-2">tous</span> },
                    { key: "price_eur", label: "Prix", render: (r) => <span className="font-mono font-bold">{Number(r.price_eur).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</span> },
                    { key: "is_active", label: "Actif", render: (r) => <StatusBadge tone={r.is_active ? "success" : "neutral"}>{r.is_active ? "actif" : "off"}</StatusBadge> },
                    { key: "actions", label: "", render: (r) => <CTAButton size="sm" variant="ghost" icon={<Trash2 className="h-3 w-3" />} onClick={() => del(r)} testId={`pricing-del-${r._id}`}>Suppr</CTAButton> },
                ]}
                rows={rules}
            />
        </>
    );
}
